<template lang="html">
  <section class="overview" @scroll.passive="setStickyState">
    <div class="notes-list" ref="notesList">
      <section v-if="notes.length === 0 && !nextPageAvailable" class="empty-state" key="empty-state">
        <h2>There are no notes to display</h2>
        <Button @click.native="$router.push('/')">Create Some</Button>
      </section>
      <section v-for="(group, key) in groupedNotes" class="note-group" :key="key">
        <h2>{{key}}</h2>
        <router-link v-for="note in group" class="mini-note" :class="{ 'new-note': note.id === 'new-note', 'dark': textColor(note.emotion) === 'dark' }" :key="note.id" :style="{backgroundImage: bgGradient(note.emotion)}" tag="div" :to="{ name: 'home', query: note.id === 'new-note' ? null : { id: note.id } }">
          <template v-if="note.id === 'new-note'">
            <Icon icon="add" />
          </template>
          <template v-else>
            {{note.createdAt | getDay}}
          </template>
        </router-link>
      </section>
      <div v-show="nextPageAvailable" class="load-more-area" key="loadMoreArea" ref="loadMoreArea">
        <Button v-show="nextPageAvailable" @click.native="fetchNotesPage">Load more</Button>
      </div>
    </div>
    <StickyFab base="settings" :base-action="() => $router.push({ query: { showSettings: true }})" :expandable="false" />
    <Modal :show="showSettings" @close="hideSettings">
      <input v-model="username" type="text" />
      <Button @click.native="$refs.dataInput.click">Import Data</Button>
      <Button @click.native="exportData">Export Data</Button>
      <Button @click.native="showPrivacyPolicy">Privacy Policy</Button>
      <Button @click.native="showChangelog">Changelog</Button>
      <Button @click.native="openImprint">Impressum</Button>
      <Button v-if="$store.state.application.installPrompt && $store.state.user.prompted" @click.native="promptInstallation">Install App</Button>
      <span>Made with 💚 by <a href="https://amxmln.com" target="_blank">Amadeus Maximilian</a></span>
      <input accept=".json" ref="dataInput" style="display: none" type="file" @change="readData">
    </Modal>
    <Modal class="loading-modal" :dismissable="false" :show="showLoading">
      <Loader :percentage="loadingPercentage" />
    </Modal>
  </section>
</template>

<script>
import { Date as SugarDate } from 'sugar-date';

import Button from '@/components/Button.vue';
import Loader from '@/components/Loader.vue';
import Icon from '@/components/Icon.vue';
import Modal from '@/components/Modal.vue';
import StickyFab from '@/components/StickyFab.vue';

import db from '@/db';

export default {
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('touchstart', this.handleSwipeStart);
    window.removeEventListener('mousedown', this.handleSwipeStart);
  },
  components: {
    Button,
    Loader,
    Icon,
    Modal,
    StickyFab,
  },
  computed: {
    groupedNotes() {
      return this.notes.reduce((accumulator, note) => {
        const groups = accumulator;
        const date = SugarDate.format(new Date(note.createdAt), '{Month} {yyyy}');

        if (!groups[date]) groups[date] = [];
        groups[date].push(note);

        return groups;
      }, {});
    },
    username: {
      get() {
        return this.$store.state.user.name;
      },
      set(value) {
        if (value) this.$store.dispatch('setName', value);
        else this.$store.dispatch('setName', 'Anon');
      },
    },
  },
  created() {
    if (this.$route.query.showSettings) this.showSettings = true;
    document.querySelector('meta[name=theme-color]').setAttribute('content', '#555D6C');
    window.addEventListener('touchstart', this.handleSwipeStart);
    window.addEventListener('mousedown', this.handleSwipeStart);
  },
  data() {
    return {
      currentPage: 0,
      hideLoadMoreAreaTimeout: null,
      loadingPercentage: 0,
      nextPageAvailable: true,
      notes: [],
      notesLoading: false,
      observer: null,
      pageSize: 50,
      showLoading: false,
      showSettings: false,
      swipeStart: { x: 0, y: 0 },
    };
  },
  filters: {
    getDay(value) {
      return SugarDate.format(new Date(value), '{dd}');
    },
  },
  methods: {
    bgGradient(emotion) {
      if (emotion) {
        const c1 = this.$store.state.application.emotions[emotion][0];
        const c2 = this.$store.state.application.emotions[emotion][1];
        return `linear-gradient(${c1}, ${c2})`;
      }
      return '';
    },
    async exportData() {
      this.showLoading = true;
      const blob = await this.$store.dispatch('exportDB', this.progressCallback);
      if (blob) {
        const tempLink = document.createElement('a');
        const filename = `256c-${new SugarDate().format('{year}-{MM}-{dd}')}.json`;
        let blobUrl;
        if (typeof window.navigator.msSaveBlob !== 'undefined') window.navigator.msSaveBlob(blob, filename);
        else {
          blobUrl = window.URL.createObjectURL(blob);
          tempLink.style.display = 'none';
          tempLink.href = blobUrl;
          tempLink.setAttribute('download', filename);
          if (typeof tempLink.download === 'undefined') tempLink.setAttribute('target', '_blank');
          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
          if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        }
      }
      this.showLoading = false;
      this.loadingPercentage = 0;
    },
    async fetchNotesPage() {
      const timeoutId = window.setTimeout(() => { this.notesLoading = true; }, 200);
      try {
        const notes = await db.notes.orderBy('createdAt')
          .filter((note) => !note.deleted)
          .reverse()
          .offset(this.currentPage * this.pageSize)
          .limit(this.pageSize)
          .toArray();

        this.currentPage += 1;
        this.nextPageAvailable = notes.length === this.pageSize;
        if (notes.length > 0) {
          if (this.notes.length === 0) this.notes.push({ createdAt: Date.now(), id: 'new-note' });
          this.notes = this.notes.concat(notes);
        }
      } catch (err) {
        this.$store.commit('addToast', { message: `There was an error loading the notes: ${err.message}` });
      } finally {
        window.clearTimeout(timeoutId);
        this.notesLoading = false;
      }
    },
    async handleSwipeEnd(e) {
      if (e.type === 'touchend') window.removeEventListener('touchend', this.handleSwipeEnd);
      if (e.type === 'mouseup') window.removeEventListener('mouseup', this.handleSwipeEnd);
      const swipeEndX = e.type === 'touchend' ? e.changedTouches[0].screenX : e.clientX;
      const swipeEndY = e.type === 'touchend' ? e.changedTouches[0].screenY : e.clientY;
      const thresholdY = 120;
      const distance = swipeEndX - this.swipeStart.x;
      const distanceY = swipeEndY - this.swipeStart.y;

      if (Math.abs(distance) < Math.abs(distanceY)) {
        if (distanceY < 0) return; // it’s a scroll
        if (distanceY > thresholdY) this.$router.push('/');
      }
    },
    handleSwipeStart(e) {
      if (this.$el.scrollTop !== 0 || this.showSettings || this.showLoading || this.$route.query.showChangelog || this.$route.query.showPrivacyPolicy) return; // we’re scrolling
      this.swipeStart.x = e.type === 'touchstart' ? e.changedTouches[0].screenX : e.clientX;
      this.swipeStart.y = e.type === 'touchstart' ? e.changedTouches[0].screenY : e.clientY;
      if (e.type === 'touchstart') window.addEventListener('touchend', this.handleSwipeEnd);
      if (e.type === 'mousedown') window.addEventListener('mouseup', this.handleSwipeEnd);
    },
    hideSettings() {
      const query = { ...this.$route.query };
      delete query.showSettings;
      this.$router.replace({ query });
    },
    openImprint() {
      const win = window.open('https://amxmln.com/legal/', '_blank');
      if (win) win.focus();
      else this.$store.commit('addToast', { message: 'Please allow pop-ups for this website since the Imprint opens in a new window' });
    },
    promptInstallation() {
      this.$store.state.application.installPrompt.prompt();
      this.$store.state.application.installPrompt.userChoice
        .then((choice) => {
          if (choice.outcome === 'accepted') this.$store.commit('addToast', { message: 'Great! Thank you for your trust.' });
          else this.$store.commit('addToast', { message: 'Should you change your mind, you can still install the application like you just did.' });
          this.$store.commit('setInstallPrompt', null);
        });
    },
    async prefillNotesAndStartObserver() {
      if (this.$el.scrollHeight <= this.$el.clientHeight && this.nextPageAvailable) {
        await this.fetchNotesPage();
        this.$nextTick(this.prefillNotesAndStartObserver);
      } else {
        this.observer = new IntersectionObserver(([entry]) => {
          if (entry && entry.isIntersecting && !this.notesLoading && this.nextPageAvailable) this.fetchNotesPage();
        });

        this.observer.observe(this.$refs.loadMoreArea);
      }
    },
    progressCallback({ completedRows, totalRows }) {
      this.loadingPercentage = Math.round((completedRows / totalRows) * 100);
    },
    async readData(e) {
      this.showLoading = true;
      const success = await this.$store.dispatch('importDB', { file: e.target.files[0], progressCallback: this.progressCallback });
      e.target.value = null; // clear the input so it can accept the same file again
      this.showLoading = false;
      this.loadingPercentage = 0;
      if (success) this.$router.push('/');
    },
    setStickyState() {
      window.requestAnimationFrame(() => {
        const stickyEls = this.$el.querySelectorAll('.note-group h2');
        const stickyOffset = 13.28;

        for (let i = 0; i < stickyEls.length; i += 1) {
          const currentEl = stickyEls[i];
          const currentOffset = currentEl.getBoundingClientRect().top;

          if (currentOffset <= stickyOffset) currentEl.classList.add('stuck');
          else currentEl.classList.remove('stuck');
        }
      });
    },
    showChangelog() {
      const query = { ...this.$route.query, showChangelog: true };
      this.$router.push({ query });
    },
    showPrivacyPolicy() {
      const query = { ...this.$route.query, showPrivacyPolicy: true };
      this.$router.push({ query });
    },
    textColor(emotion) {
      // calculate text color based on background
      // adapted from https://awik.io/determine-color-bright-dark-using-javascript/
      let color;
      if (emotion) [color] = this.$store.state.application.emotions[emotion];
      else color = '#555D6C';
      if (color.length === 4) color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`; // eslint-disable-line no-param-reassign

      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

      if (hsp > 200) return 'dark';
      return 'light';
    },
  },
  mounted() {
    this.prefillNotesAndStartObserver();
  },
  watch: {
    $route(newVal) {
      if (newVal.query.showSettings && !this.showSettings) this.showSettings = true;
      if (!newVal.query.showSettings && this.showSettings) this.showSettings = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '../styles/colors'
@require '../styles/shadows'

.overview
  overflow: auto
  height: 100%

  &.fade-enter-active .notes-list
    transition: opacity 250ms ease, transform 250ms ease

  &.fade-enter .notes-list
    transform: translateY(64px)
    opacity: 0

  .notes-list

    .load-more-area
      margin: 8px
      height: 48px
      display: flex
      justify-content: center
      align-items: center
      color: alpha($text, 0.5)
      text-transform: uppercase
      letter-spacing: 1px
      border-radius: 12px

    .empty-state
      height: 100vh
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center

      h2
        color: alpha($text, 0.5)
        text-transform: uppercase
        letter-spacing: 1px
        margin-top: 0

    .note-group
      margin-bottom: 48px
      margin-left: auto
      margin-right: auto
      width: 100%

      @media screen and (min-width: 256px)
        width: 256px

      @media screen and (min-width: 320px)
        width: 320px

      @media screen and (min-width: 384px)
        width: 384px

      @media screen and (min-width: 448px)
        width: 448px

      @media screen and (min-width: 512px)
        width: 512px

      @media screen and (min-width: 576px)
        width: 576px

      @media screen and (min-width: 640px)
        width: 640px

      h2
        text-align: center
        text-transform: uppercase
        letter-spacing: 1px
        position: sticky
        top: 0.83em
        z-index: 1
        padding: 0 16px
        border-radius: 12.5px
        width: max-content
        margin-left: auto
        margin-right: auto
        margin-top: 2rem
        transition: background-color 200ms ease, color 200ms ease, box-shadow 200ms ease

        &.stuck
          background-color: alpha($text, 0.8)
          color: alpha($black, 0.7)
          text-shadow: none
          box-shadow: $interactable-shadow

      .mini-note
        width: 48px
        height: 56px
        align-items: center
        justify-content: center
        border-radius: 6px
        box-shadow: $interactable-shadow
        display: inline-flex
        margin: 8px
        cursor: pointer

        &.new-note
          vertical-align: top
          border: 2px dashed alpha($text, 0.5)

        &.dark
          color: alpha($black, 0.5)

  .sticky-fab
    position: fixed
    bottom: 32px
    left: calc(50% - 28px)
    z-index: 2

  .modal
    &.loading-modal
      >>> h1,
      >>> h2
        margin: 0

      >>> h1
        font-size: 2.5rem

      >>> h2
        margin-top: -10px

    input
      width: 100%
      margin-bottom: 48px

    .button
      width: 100%
      margin-bottom: 16px
      display: block

      &:last-of-type
        margin-bottom: 48px

    span
      opacity: 0.5
      text-transform: uppercase
      letter-spacing: 1px
      font-size: 0.75rem
      display: block
      width: 100%
      text-align: center

      a
        color: inherit
</style>
